<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :title="getModalTitle()"
    :hide-save-button="!modifiedData.isFromManager || viewOnly"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div v-if="representativeName" class="pointer-events-none flex flex-col">
        <label for="representative" class="text-sm font-medium">
          {{ $t('representative.representative') }} - {{ representativeName }}
        </label>
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="remoteCode" class="text-sm font-medium">
          {{ $t('transactions.remoteCode') }}
        </label>
        <BaseInput
          v-model="modifiedData.measurementUnitRemotedCode"
          color="base"
          :disabled="!modifiedData.isFromManager || mode === 'assign' || viewOnly"
          show-error-as-tooltip
          :error="
            $v.measurementUnitRemotedCode.required.$invalid
              ? $v.measurementUnitRemotedCode.required.$message
              : $v.measurementUnitRemotedCode.uniqueRemoteCode.$invalid
                ? $v.measurementUnitRemotedCode.uniqueRemoteCode.$message
                : ''
          " />
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="measurementUnit" class="text-sm font-medium">{{
          $t('material.measurementUnit')
        }}</label>
        <BaseSearchDropdown
          :list-items="dropdownMeasurementUnits"
          v-model="selectedMeasurementUnit"
          :no-results-text="$t('messages.noResultsText')"
          name="measurementUnit"
          show-error-as-tooltip
          internal-filter
          :disabled="!modifiedData.isFromManager || viewOnly"
          :error="
            $v.measurementUnitId.required.$invalid ? $v.measurementUnitId.required.$message : ''
          "
          @item-selected="handleMeasurementUnitSelection"
          @clear-selection="handleMeasurementUnitSelection" />
      </div>

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }"
        :label="$t('baseObject.isActive')"
        name="isActive"
        :disable="mode === 'assign' && !!measurementUnit.measurementUnitId"
        v-model="modifiedData.isActive" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseToggle from '../common/BaseToggle.vue';
import BaseSearchDropdown from '../common/BaseSearchDropdown.vue';

import { type SelectDropdownItem, RecordMode } from '@/types/Base';
import type { DCMeasurementUnit } from '@/types/DataCollection';

const { t: $t } = useI18n({ useScope: 'global' });
const { getMeasurementUnitsLookup } = useLookupsStore();
const { measurementUnits: lookupMeasurementUnits, dropdownMeasurementUnits } =
  storeToRefs(useLookupsStore());

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit' | 'assign' | 'view';
    measurementUnit: DCMeasurementUnit;
    representativeName?: string;
    existingMeasurementUnits?: DCMeasurementUnit[];
    viewOnly?: boolean;
  }>(),
  {}
);

const modifiedData = ref<DCMeasurementUnit>(deepCopy<DCMeasurementUnit>(props.measurementUnit));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.measurementUnitId = newData.measurementUnitId || '';
    validationModel.value.measurementUnitRemotedCode = newData.measurementUnitRemotedCode || '';

    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.measurementUnit);
  },
  { deep: true }
);

const selectedMeasurementUnit = ref<SelectDropdownItem | null>(null);

function handleMeasurementUnitSelection(item: SelectDropdownItem) {
  selectedMeasurementUnit.value = item ? item : null;
  if (!modifiedData.value) return;
  modifiedData.value.measurementUnitId = item ? item.value.toString() : '';

  const measurementUnit = lookupMeasurementUnits.value.find((x) => x.id === item.value);
  if (measurementUnit) modifiedData.value.measurementUnit = measurementUnit;
}

function onDismissChanges() {
  modifiedData.value = deepCopy<DCMeasurementUnit>(props.measurementUnit);
  emit('closeModal');
}

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  emit('save', modifiedData.value);
}

onMounted(async () => {
  await getMeasurementUnitsLookup();

  if (modifiedData.value && modifiedData.value.measurementUnitId) {
    const selectedValue = dropdownMeasurementUnits.value.find(
      (item) => item.value === modifiedData.value?.measurementUnitId
    );

    if (selectedValue) {
      selectedMeasurementUnit.value = selectedValue;
    }
  }
});

const validationModel = ref({
  measurementUnitId: modifiedData.value.measurementUnitId || '',
  measurementUnitRemotedCode: modifiedData.value.measurementUnitRemotedCode || '',
});

// Custom validator for uniqueness
const isUniqueRemoteCode = (value: string) => {
  if (!value || !props.existingMeasurementUnits) return true;

  const existingDuplicate = props.existingMeasurementUnits.find(
    (mu) =>
      mu.id !== modifiedData.value.id &&
      mu.measurementUnitRemotedCode === value &&
      mu.recordMode !== RecordMode.Deleted
  );

  return !existingDuplicate;
};

const rules = computed(() => ({
  measurementUnitId: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
  measurementUnitRemotedCode: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
    uniqueRemoteCode: helpers.withMessage(
      $t('messages.errors.duplicateRemoteCode'),
      isUniqueRemoteCode
    ),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function getModalTitle() {
  const actionKey =
    props.viewOnly || props.mode === 'view' ? 'view' : props.mode === 'add' ? 'create' : 'edit';
  return `${$t(`common.actions.${actionKey}`)} ${$t('representative.entities.measurementUnit')}`;
}
</script>
